import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import logger from '../../utils/logging';
import { API_BASE_URL } from '../../config/api';
import analyticsService from '../../services/analyticsService';
import { trackPurchaseConversion } from '../../adPixels';

const ORDER_RECEIPT_STORAGE_PREFIX = 'rs_order_receipt_v1:';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { isAuthenticated } = useCustomerAuth();
  const canvasRef = useRef(null);
  const cartClearedRef = useRef(false);
  const purchaseTrackedRef = useRef(false);
  
  const orderNumber = searchParams.get('order');
  const printfulOrderId = searchParams.get('printful');
  const [receipt, setReceipt] = useState(null);
  const isDonationOrder = receipt?.isDonationOnly === true;
  const digitalDownloadGrants = Array.isArray(receipt?.digitalDownloadGrants)
    ? receipt.digitalDownloadGrants
    : [];
  const [thankYouLine, setThankYouLine] = useState('');
  const [thankYouLoading, setThankYouLoading] = useState(false);

  useEffect(() => {
    if (!orderNumber) return;
    try {
      const raw = sessionStorage.getItem(ORDER_RECEIPT_STORAGE_PREFIX + orderNumber);
      if (raw) setReceipt(JSON.parse(raw));
    } catch {
      setReceipt(null);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (!orderNumber) return;
    let fromStore = null;
    try {
      const raw = sessionStorage.getItem(ORDER_RECEIPT_STORAGE_PREFIX + orderNumber);
      if (raw) fromStore = JSON.parse(raw);
    } catch {
      fromStore = null;
    }
    const rec = fromStore || receipt;
    const businessId = rec?.businessId || searchParams.get('bid') || '';
    if (!businessId) return;
    const firstName = rec?.firstName || searchParams.get('fn') || '';
    const donation =
      rec?.isDonationOnly === true || rec?.orderKind === 'donation';
    const orderType = rec?.orderKind || (donation ? 'donation' : 'product');
    const lineSummary =
      rec?.lineSummary ||
      (rec?.items || [])
        .map((it) => it.name)
        .filter(Boolean)
        .join(' · ') ||
      '';
    let cancelled = false;
    (async () => {
      setThankYouLoading(true);
      try {
        const base = (API_BASE_URL || '').replace(/\/$/, '');
        const res = await fetch(`${base}/shop/${encodeURIComponent(businessId)}/compose-thank-you`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: firstName || undefined,
            lineSummary: lineSummary || 'your order',
            orderType: orderType || undefined,
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.line) setThankYouLine(String(data.line));
      } catch {
        /* keep static subtext */
      } finally {
        if (!cancelled) setThankYouLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderNumber, receipt, searchParams]);
  
  useEffect(() => {
    logger.logComponentMount('CheckoutSuccess', {
      orderNumber: orderNumber,
      printfulOrderId: printfulOrderId
    });
    
    // Clear cart on success (only once)
    if (orderNumber && !cartClearedRef.current) {
      clearCart();
      cartClearedRef.current = true;
    }
    
    logger.log('info', 'checkout', 'Order completed successfully', {
      orderNumber: orderNumber,
      printfulOrderId: printfulOrderId
    });
  }, [orderNumber, printfulOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire purchase-completion tracking once the receipt (order total/items) is available.
  // Feeds both the in-house analytics dashboard (analyticsService.trackPurchase — previously
  // defined but never called anywhere, so completed-sale events never reached the funnel data)
  // and the Meta Pixel / GA4 conversion events ads need to optimize spend toward buyers.
  useEffect(() => {
    if (!receipt || !orderNumber || purchaseTrackedRef.current) return;
    if (receipt.isDonationOnly) return; // not a product purchase — skip ad conversion noise
    purchaseTrackedRef.current = true;

    try {
      analyticsService.trackPurchase({
        orderId: receipt.orderId,
        orderNumber: receipt.orderNumber,
        total: receipt.total,
        items: (receipt.items || []).map((it) => ({
          productId: it.id,
          productName: it.name,
          quantity: it.quantity,
          price: it.price,
        })),
      });
    } catch {
      /* non-critical — order already succeeded */
    }

    try {
      trackPurchaseConversion({
        orderId: receipt.orderId,
        value: Number(receipt.total) || 0,
        currency: 'AUD',
        items: (receipt.items || []).map((it) => ({
          id: it.id,
          name: it.name,
          quantity: it.quantity,
          price: it.price,
        })),
      });
    } catch {
      /* non-critical — order already succeeded */
    }
  }, [receipt, orderNumber]);

  // Matrix Rain Effect (from Home page)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const matrix = "ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789@#$%^&*()*&^%+-/~{[|`]}";
    const matrixArray = matrix.split("");
    const fontSize = 8;
    const columns = canvas.width / fontSize;
    const drops = [];
    
    for (let x = 0; x < columns; x++) {
      drops[x] = 1;
    }
    
    let hue = 0;
    let bgHue = 180;
    
    function draw() {
      ctx.fillStyle = `hsla(${bgHue}, 70%, 5%, 0.05)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      bgHue = (bgHue + 0.3) % 360;
      
      for (let i = 0; i < drops.length; i++) {
        const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];
        const columnHue = (hue + i * 5) % 360;
        ctx.fillStyle = `hsla(${columnHue}, 100%, 60%, 0.3)`;
        ctx.font = fontSize + 'px monospace';
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      hue = (hue + 1) % 360;
    }
    
    const interval = setInterval(draw, 50);
    return () => clearInterval(interval);
  }, []);
  
  if (!orderNumber) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center'
        }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>
            ⚠️ No Order Found
          </h2>
          <p style={{ color: '#d1d5db', marginBottom: '2rem' }}>
            No order information available.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      background: 'radial-gradient(ellipse at center, #0c1445 0%, #1a1a2e 50%, #16213e 100%)',
      overflow: 'auto',
      padding: '2rem'
    }}>
      {/* Matrix Rain Background */}
      <canvas 
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />

      {/* Night Sky with Moon (from Events page) */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        {/* Moon */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '15%',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle at 30% 30%, #fbbf24, #f59e0b)',
          borderRadius: '50%',
          boxShadow: '0 0 50px rgba(251, 191, 36, 0.3)',
          animation: 'float 6s ease-in-out infinite'
        }} />
        
        {/* Stars */}
        {Array.from({ length: 50 }, (_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              background: 'white',
              borderRadius: '50%',
              animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
        
        {/* Shooting Stars */}
        {Array.from({ length: 3 }, (_, i) => {
          const star = {
            id: i,
            size: Math.random() * 2 + 1,
            left: Math.random() * 100,
            top: Math.random() * 100,
            duration: Math.random() * 2 + 3,
            delay: Math.random() * 5,
            colors: ['#fbbf24', '#f59e0b', '#d97706']
          };
          
          return (
            <div key={`shooting-${i}`}>
              <div style={{
                position: 'absolute',
                left: `${star.left}%`,
                top: `${star.top}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                background: star.colors[0],
                borderRadius: '50%',
                animation: `shootingStar${star.id} ${star.duration}s linear infinite`,
                animationDelay: `${star.delay}s`
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                right: `${star.size}px`,
                transform: 'translateY(-50%)',
                width: `${star.size * 8}px`,
                height: `${star.size / 2}px`,
                background: `linear-gradient(90deg, transparent, ${star.colors[0]}40, ${star.colors[1]}60, ${star.colors[2]}80)`,
                borderRadius: '1px',
                animation: `shootingTrail${star.id} ${star.duration}s linear infinite`,
                animationDelay: `${star.delay}s`
              }} />
            </div>
          );
        })}
      </div>

      {/* Geometric Shapes (from Gallery page) */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 3,
        pointerEvents: 'none'
      }}>
        {[
          { size: 80, left: 10, top: 20, rotation: 45, delay: 1 },
          { size: 120, left: 80, top: 15, rotation: 90, delay: 2 },
          { size: 100, left: 30, top: 50, rotation: 225, delay: 3 },
          { size: 125, left: 70, top: 60, rotation: 270, delay: 4 },
          { size: 115, left: 45, top: 85, rotation: 315, delay: 5 }
        ].map((shape, i) => (
          <div
            key={`shape-${i}`}
            style={{
              position: 'absolute',
              width: `${shape.size}px`,
              height: `${shape.size}px`,
              left: `${shape.left}%`,
              top: `${shape.top}%`,
              background: `linear-gradient(45deg, 
                hsla(${(i * 45 + 180) % 360}, 80%, 70%, 0.2), 
                hsla(${(i * 45 + 270) % 360}, 80%, 70%, 0.1)
              )`,
              borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '20%' : '0%',
              animation: `floatShape ${4 + i}s ease-in-out infinite`,
              animationDelay: `${shape.delay}s`,
              transform: `rotate(${shape.rotation}deg)`,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            padding: '3rem',
            background: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            position: 'relative'
          }}>
        {/* Success Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          margin: '0 auto 2rem',
          background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '3rem',
          boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          ✅
        </div>
        
        {/* Success Message */}
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          background: 'linear-gradient(45deg, #8b5cf6, #06b6d4)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent'
        }}>
          {isDonationOrder ? 'Thank you!' : 'Order Confirmed!'}
        </h1>
        
        <p style={{
          fontSize: '1.125rem',
          color: '#1e293b',
          marginBottom: '2rem',
          minHeight: thankYouLoading ? '3rem' : undefined,
        }}>
          {thankYouLoading && !thankYouLine
            ? '…'
            : thankYouLine || (isDonationOrder
            ? "Your support means the world to us — your contribution has been received."
            : "Thank you for your order! We're excited to get it to you.")}
        </p>
        
        {/* Order Number */}
        <div style={{
          padding: '1.5rem',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          marginBottom: '2rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#64748b',
            marginBottom: '0.5rem'
          }}>
            Order Number
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1e293b',
            fontFamily: 'monospace'
          }}>
            {orderNumber}
          </div>
        </div>

        {receipt && (
          <div
            style={{
              marginBottom: '2rem',
              padding: '1.25rem',
              borderRadius: '12px',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              background: 'rgba(255,255,255,0.85)',
              textAlign: 'left',
              color: '#0f172a',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>Itemized receipt</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0' }}>Item</th>
                  <th style={{ textAlign: 'right', padding: '0.35rem 0' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '0.35rem 0' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '0.35rem 0' }}>Line</th>
                </tr>
              </thead>
              <tbody>
                {(receipt.items || []).map((line, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.35rem 0' }}>{line.name}</td>
                    <td style={{ textAlign: 'right' }}>{line.quantity}</td>
                    <td style={{ textAlign: 'right' }}>${Number(line.price).toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>${Number(line.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Subtotal</span>
                <span>${Number(receipt.subtotal).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Shipping</span>
                <span>${Number(receipt.shipping).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>GST (included in prices)</span>
                <span>${Number(receipt.tax).toFixed(2)}</span>
              </div>
              {Number(receipt.discount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#15803d' }}>
                  <span>Discount</span>
                  <span>-${Number(receipt.discount).toFixed(2)}</span>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                  paddingTop: '0.5rem',
                  borderTop: '2px solid #cbd5e1',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                <span>Total (AUD)</span>
                <span>${Number(receipt.total).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Status Message */}
        <div style={{
          padding: '1.5rem',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          marginBottom: '2rem'
        }}>
          <div style={{
            fontSize: '1.125rem',
            color: '#8b5cf6',
            marginBottom: '0.75rem',
            fontWeight: '600'
          }}>
            {isDonationOrder
              ? '💝 Donation received'
              : (printfulOrderId || receipt?.printfulOrderId)
                ? '🎨 Your order is being printed!'
                : '📦 Order received!'}
          </div>
          <p style={{
            fontSize: '0.95rem',
            color: '#1e293b',
            margin: 0,
            lineHeight: '1.6'
          }}>
            {isDonationOrder
              ? 'There is nothing to ship — this was a digital contribution. Your payment is complete.'
              : (printfulOrderId || receipt?.printfulOrderId)
                ? 'Your order has been automatically sent to production and will be shipped soon.'
                : 'Your order is being reviewed and will be processed shortly.'}
          </p>
          
          {!isDonationOrder && (printfulOrderId || receipt?.printfulOrderId) && (
            <div style={{
              marginTop: '1rem',
              fontSize: '0.875rem',
              color: '#64748b'
            }}>
              Printful Order ID: {printfulOrderId || receipt?.printfulOrderId}
            </div>
          )}
        </div>

        {digitalDownloadGrants.length > 0 && (
          <div
            style={{
              padding: '1.5rem',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.35)',
              marginBottom: '2rem',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: '1.05rem',
                color: '#1d4ed8',
                marginBottom: '0.75rem',
                fontWeight: '600',
              }}
            >
              Your downloads
            </div>
            <p style={{ fontSize: '0.92rem', color: '#334155', margin: '0 0 1rem', lineHeight: 1.6 }}>
              Use the links below to access your file(s). Save this page or bookmark — the links expire after a period of time.
            </p>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: 1.9 }}>
              {digitalDownloadGrants.map((g) => (
                <li key={(g.token || '') + String(g.downloadIndex ?? '')}>
                  <a
                    href={`${(API_BASE_URL || '').replace(/\/$/, '')}/orders/digital-download?token=${encodeURIComponent(g.token)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2563eb', fontWeight: 600 }}
                  >
                    {[
                      g.productName || 'Download',
                      g.label ? ` — ${g.label}` : null,
                      g.downloadIndex > 0 && !g.label ? ` (part ${Number(g.downloadIndex) + 1})` : null,
                    ]
                      .filter(Boolean)
                      .join('')}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Delivery — physical orders only */}
        {!isDonationOrder && (
        <div style={{
          padding: '1.5rem',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          marginBottom: '2rem'
        }}>
          <div style={{
            fontSize: '1rem',
            color: '#1e293b',
            marginBottom: '0.5rem',
            fontWeight: '600'
          }}>
            📅 Estimated Delivery
          </div>
          <p style={{
            fontSize: '0.95rem',
            color: '#1e293b',
            margin: 0
          }}>
            7-10 business days
          </p>
          <p style={{
            fontSize: '0.875rem',
            color: '#64748b',
            marginTop: '0.5rem',
            margin: 0
          }}>
            You'll receive a shipping confirmation with tracking once your order ships.
          </p>
        </div>
        )}
        
        {/* Next Steps */}
        <div style={{
          padding: '1.5rem',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          marginBottom: '2rem',
          textAlign: 'left'
        }}>
          <div style={{
            fontSize: '1rem',
            color: '#8b5cf6',
            marginBottom: '0.75rem',
            fontWeight: '600'
          }}>
            {isDonationOrder ? '📧 What happens next' : "📧 What's Next?"}
          </div>
          <ul style={{
            fontSize: '0.95rem',
            color: '#1e293b',
            margin: 0,
            paddingLeft: '1.5rem',
            lineHeight: '1.8'
          }}>
            {isDonationOrder ? (
              <>
                <li>Check your email for a payment receipt</li>
                <li>Your donation supports Rainbow Sails — thank you</li>
              </>
            ) : (
              <>
                <li>Check your email for order confirmation</li>
                <li>We&apos;ll notify you when your order ships</li>
                <li>Track your package with the tracking number</li>
                <li>Enjoy your Rainbow Sails product! 🌈</li>
              </>
            )}
          </ul>
        </div>

        {/* Guest checkout — sign up from this order for support + tracking */}
        {!isDonationOrder && !isAuthenticated && orderNumber && (
          <div
            style={{
              padding: '1.5rem',
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(59, 130, 246, 0.12))',
              borderRadius: '12px',
              border: '2px solid rgba(16, 185, 129, 0.45)',
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f766e', marginBottom: '0.5rem' }}>
              Ordered as a guest?
            </div>
            <p style={{ fontSize: '0.95rem', color: '#334155', margin: '0 0 1rem', lineHeight: 1.6 }}>
              Create a free account with the <strong>same email you used at checkout</strong> to see this order, get updates, and
              open customer service when you need help.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              <Link
                to={`/customer/signup?return=${encodeURIComponent('/customer/profile?tab=orders')}`}
                style={{
                  padding: '0.65rem 1.25rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                Sign up from this order
              </Link>
              <Link
                to={`/customer/login?return=${encodeURIComponent('/customer/profile?tab=orders')}`}
                style={{
                  padding: '0.65rem 1.25rem',
                  background: 'white',
                  color: '#0f172a',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-block',
                  border: '1px solid #cbd5e1',
                }}
              >
                Sign in
              </Link>
              <Link
                to="/contact"
                style={{
                  padding: '0.65rem 1.25rem',
                  color: '#4f46e5',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  display: 'inline-block',
                  alignSelf: 'center',
                }}
              >
                Contact page
              </Link>
            </div>
          </div>
        )}
        
        {/* Action Buttons — in-app links (account + support) */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          justifyContent: 'center'
        }}>
          {isAuthenticated ? (
            <>
          <Link
            to="/customer/profile?tab=orders"
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
              color: 'white',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            View orders in your account
          </Link>
          <Link
            to="/customer/profile?tab=conversations"
            style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(15, 23, 42, 0.85)',
              color: 'white',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            Message Rainbow Sails
          </Link>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => {
              console.log('🔍 Button clicked - navigating to home');
              navigate('/');
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            Continue exploring
          </button>
        </div>
        
        {/* Support Info */}
        <p style={{
          fontSize: '0.875rem',
          color: '#64748b',
          marginTop: '2rem'
        }}>
          Need help? Visit our{' '}
          <Link to="/contact" style={{ color: '#6366f1', fontWeight: 600 }}>
            Contact
          </Link>
          page — or{' '}
          {isAuthenticated ? (
            <Link to="/customer/profile?tab=support" style={{ color: '#6366f1', fontWeight: 600 }}>
              open customer service
            </Link>
          ) : (
            <Link
              to={`/customer/login?return=${encodeURIComponent('/customer/profile?tab=support')}`}
              style={{ color: '#6366f1', fontWeight: 600 }}
            >
              sign in for customer service
            </Link>
          )}
          {' '}in My Account. Email support@rainbow-sails.shop.
        </p>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes twinkle {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
        
        @keyframes floatShape {
          0%, 100% {
            transform: rotate(var(--rotation, 0deg)) translateY(0px);
          }
          50% {
            transform: rotate(var(--rotation, 0deg)) translateY(-20px);
          }
        }
        
        @keyframes shootingStar0 {
          0% {
            transform: translateX(-100px) translateY(100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(100px) translateY(-100px);
            opacity: 0;
          }
        }
        
        @keyframes shootingStar1 {
          0% {
            transform: translateX(-150px) translateY(150px);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            transform: translateX(150px) translateY(-150px);
            opacity: 0;
          }
        }
        
        @keyframes shootingStar2 {
          0% {
            transform: translateX(-120px) translateY(120px);
            opacity: 0;
          }
          12% {
            opacity: 1;
          }
          88% {
            opacity: 1;
          }
          100% {
            transform: translateX(120px) translateY(-120px);
            opacity: 0;
          }
        }
        
        @keyframes shootingTrail0 {
          0% {
            transform: translateY(-50%) translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-50%) translateX(100px);
            opacity: 0;
          }
        }
        
        @keyframes shootingTrail1 {
          0% {
            transform: translateY(-50%) translateX(-150px);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            transform: translateY(-50%) translateX(150px);
            opacity: 0;
          }
        }
        
        @keyframes shootingTrail2 {
          0% {
            transform: translateY(-50%) translateX(-120px);
            opacity: 0;
          }
          12% {
            opacity: 1;
          }
          88% {
            opacity: 1;
          }
          100% {
            transform: translateY(-50%) translateX(120px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CheckoutSuccess;

